﻿import * as _ from "lodash";
import moment from "moment";
import React from 'react';
import ReactDom from "react-dom/client";
import JsxParser from "@recursive-robot/react-jsx-parser";
import styled from "@emotion/styled";
import browserHistory from "../../shared/history";
import { getColumnDateTimeFormatter, getColumnNumberFormatter, formatDate, formatNumber, getNavigateToPageHandler } from "../../shared/utilities";
import { createSelector } from "@reduxjs/toolkit";
import { RootState, useAppSelector } from "../../store";
import { useAppActions } from "../../shared/hooks";
import { TemplateDefinition } from "../../store/storyline/types";
import "./Canvas.scss";
import { CanvasBindingsContext } from "../../shared/providers/CanvasBindingsProvider";
import { StaticPlotContext } from "../../shared/providers/StaticPlotProvider";
import { AllowSideEffectsContext } from "../../shared/providers/AllowSideEffectsProvider";
import { useSettings } from "../../shared/providers/SettingsProvider";
import * as FileSaver from "file-saver";
import clsx from "clsx";
import * as xlsx from "xlsx";
import * as Papa from "papaparse";
import { default as Color } from "colorjs.io";
import { logger, SeverityLevel } from "../../shared/services/loggingService";
import { NavFiltersParameterName } from "../components/NavFilters";
import { useAuth } from "../../auth/AuthContext";
import { TooltipContainerContext } from "../../shared/providers/TooltipContainerProvider";
import { ErrorContainer } from "../../shared/pages/ErrorContainer";
import * as namedColors from "../../shared/colors";
import { useJsxParserComponents } from "../../shared/providers/JsxParserComponentsProvider";
import { CircularProgress } from "../../shared/components";
import { NotificationBanner } from "./NotificationBanner";
import { useWebSocketService } from "../../shared/providers/WebSocketServiceProvider";
import { useTemplateDiagnostics } from "../../shared/hooks/useTemplateDiagnostics";
import { useProfilingSink } from "../../shared/hooks/useProfilingSink";
import { compare } from "compare-versions";
import { snapdom } from "@zumer/snapdom";


const blacklistedAttrs = [];
const blacklistedTags = [];
// Library handles exposed to template bindings never change, so they're shared at module level...
const staticLibraries = {
    moment,
    FileSaver,
    Object,
    xlsx,
    _,
    browserHistory,
    ReactDom,
    Papa,
    logger,
    Color,
    snapdom,
};
// Module-scoped so all Canvas instances (main canvas + minimap previews) share one
// computation and one object identity per parameter-map version...
const populatedParameterValuesSelector = createSelector(
    [(state: RootState) => state.storyline.parameterValues],
    parameterValues => _.omitBy(Object.fromEntries(parameterValues), _.isUndefined)
);
const renderError = ({ error }) => {
    logger.trackException({ exception: error, severityLevel: SeverityLevel.Error });

    return (
        <ErrorContainer
            error={{
                type: "Generic",
                message: "An error occurred while parsing template.",
                details: error.toString()
            }}
        />
    );
};

interface Props {
    template: TemplateDefinition;
    templateName?: string;
    frameData: any;
    staticPlot: boolean;
    allowSideEffects?: boolean;
}

const Canvas: React.FC<Props> = (props) => {
    const { template, templateName, frameData, staticPlot, allowSideEffects = true } = props;

    const parameterValues = useAppSelector(state => state.storyline.parameterValues);
    const canvasState = useAppSelector(state => state.storyline.canvasState);
    const datasourcesInFlight = useAppSelector(state => state.storyline.datasourcesInFlight);
    const activeBranch = useAppSelector(state => state.app.activeBranch);
    const actions = useAppActions();

    const components = useJsxParserComponents();
    const settings = useSettings();
    const { userMetadata } = useAuth();
    const webSocketService = useWebSocketService();

    const containerRef = React.useRef<HTMLDivElement>(null);

    const onError = useTemplateDiagnostics(templateName, template?.jsx);
    const { onProfile, enabled: profilingEnabled, reactRenderEnabled } = useProfilingSink();

    // Tag parameter updates made explicitly from template-author scripts (e.g. onClick arrow functions)
    // as "UpdateParameterValueCall" so they are distinguishable from built-in input-control changes in tracing.
    // Input components dispatch updateParameterValue directly and are unaffected by this wrapper.
    const updateParameterValue = React.useCallback(
        (parameterName: string, newValueOrTransformer: unknown, isInitialLoad = false, preventDatasourceRefresh = false, sourceDatasourceId: string | null = null) =>
            actions.updateParameterValue(parameterName, newValueOrTransformer, isInitialLoad, preventDatasourceRefresh, sourceDatasourceId, "UpdateParameterValueCall"),
        [actions]);

    const populatedParameterValues = useAppSelector(populatedParameterValuesSelector);
    const navigateToPage = React.useCallback(getNavigateToPageHandler(populatedParameterValues, NavFiltersParameterName, browserHistory), [populatedParameterValues]);

    const functions = React.useMemo(() => ({
        // Dispatch-wrapped app actions...
        ...actions,

        // Backwards-compatibility aliases + wrappers...
        updateParameterValue,
        updateParameterValueAction: updateParameterValue,
        applyParameterValueChangesAction: actions.applyParameterValueChanges,
        navigateToPage,
        trackEvent: logger.trackEvent.bind(logger),
        trackException: logger.trackException.bind(logger),
        trackTrace: logger.trackTrace.bind(logger),
        trackMetric: logger.trackMetric.bind(logger),

        // Helper functions...
        getColumnNumberFormatter,
        getColumnDateTimeFormatter,
        clsx,
        formatNumber,
        formatDate,
        compareVersions: compare,
    }), [actions, updateParameterValue, navigateToPage]);

    const libraries = React.useMemo(() => ({
        ...staticLibraries,
        webSocketService,
    }), [webSocketService]);

    const metadata = React.useMemo(() => ({
            userMetadata,
            settings,
            datasourcesInFlight,
            namedColors,
            appMetadata: {
                activeBranch
            },
            allowSideEffects,
    }), [userMetadata, settings, datasourcesInFlight, activeBranch, allowSideEffects]);

    const categorizedBindings = React.useMemo(() => ({
        canvasState,
        frameData,
        parameterValues: populatedParameterValues,
        functions,
        libraries,
        metadata,
    }), [canvasState, frameData, populatedParameterValues, functions, libraries, metadata]);

    const bindings = React.useMemo(() => ({
        ...categorizedBindings.canvasState,
        template,
        templateName,
        frameData,
        staticPlot,
        allowSideEffects,
        parameterValues,
        canvasState,
        datasourcesInFlight,
        activeBranch,
        ...categorizedBindings.frameData,
        ...categorizedBindings.parameterValues,
        ...categorizedBindings.functions,
        ...categorizedBindings.libraries,
        ...categorizedBindings.metadata,
    }), [categorizedBindings, template, templateName, frameData, staticPlot, allowSideEffects, parameterValues, canvasState, datasourcesInFlight, activeBranch]);

    // Scopes the page's authored CSS under an emotion-generated class on the root div (selectors become descendant-scoped)...
    const ScopedRoot = React.useMemo(() => template?.css ? styled.div(template.css) : null, [template?.css]);

    if (!frameData) return <CircularProgress className="loading-spinner" />;

    window["canvasBindings"] = categorizedBindings;

    const Container = ScopedRoot ?? "div";

    return <Container className="jsx-parser" ref={containerRef}>
        <StaticPlotContext.Provider value={staticPlot}>
            <AllowSideEffectsContext.Provider value={allowSideEffects}>
                <TooltipContainerContext.Provider value={containerRef}>
                    <CanvasBindingsContext.Provider value={bindings}>
                        <JsxParser
                            bindings={bindings}
                            components={components}
                            jsx={template?.jsx}
                            fileName={templateName}
                            blacklistedAttrs={blacklistedAttrs}
                            blacklistedTags={blacklistedTags}
                            onError={onError}
                            onProfile={profilingEnabled ? onProfile : undefined}
                            profileReactRender={profilingEnabled && reactRenderEnabled}
                            renderError={renderError}
                            renderInWrapper={false}
                            key={`${templateName}:${template?.version ?? 0}`} // Force re-parse when template content is updated (version is bumped on every template push)
                        />
                        { !allowSideEffects && <NotificationBanner key="notification-banner" /> }
                    </CanvasBindingsContext.Provider>
                </TooltipContainerContext.Provider>
            </AllowSideEffectsContext.Provider>
        </StaticPlotContext.Provider>
    </Container>;
};

export default React.memo(Canvas);